import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { EntityType } from '@common/api';
import { Text } from '@components/typography/Typography';
import { useEntity } from '@data/entity/entity';
import { PseudoEntityRow, GeneratedEntity, GeneratedEntityType } from '@data/entity/types';
import { getShortKeyForEntity, toRealEntityKey } from '@data/entity/util';
import { useI18nContext } from '@i18n/i18n-react';

import { SidebarTreeRow, SidebarTreeExpand, SidebarTreeLabel, SidebarTreeTag } from '@components/layout/Sidebar';
import { TagsDraggable } from './TagsDraggable';

const INDENT = 20;

export type TagsTreeItemProps = {
    item: PseudoEntityRow;
    isExpanded: (path: string[]) => boolean;
    toggleExpand: (path: string[]) => void;
};

export const TagsTreeItem = (props: TagsTreeItemProps) => {
    const { item, isExpanded, toggleExpand } = props;
    const { LL } = useI18nContext();

    // Item may be blank on last empty line
    const entity = useEntity(toRealEntityKey(item?.key)) ?? (item?.key as GeneratedEntity);
    if (!item) return null;

    const { key, depth, path, canExpand } = item;
    const { type, meta } = entity;

    const ROOT_FOLDER_LABELS: Record<string, string> = {
        Dimensions: LL.DIMENSIONS(),
        Variables: LL.VARIABLES(),
    };
    const folderName = ROOT_FOLDER_LABELS[meta?.name ?? ''] ?? meta?.name ?? '';

    const indent = depth * INDENT;
    const expanded = isExpanded(path);

    const handleClick = canExpand ? () => toggleExpand(path) : undefined;

    const expandNode = canExpand ? (
        <SidebarTreeExpand>{expanded ? <FaChevronDown /> : <FaChevronRight />}</SidebarTreeExpand>
    ) : (
        <SidebarTreeExpand />
    );

    const labelNode =
        type === EntityType.Folder ? (
            <SidebarTreeLabel>{folderName}</SidebarTreeLabel>
        ) : type === GeneratedEntityType.Literal ? (
            <SidebarTreeLabel>
                <Text $muted>{meta?.name ?? ''}</Text>
            </SidebarTreeLabel>
        ) : (
            <SidebarTreeTag>
                <TagsDraggable entity={entity} />
            </SidebarTreeTag>
        );

    return (
        <SidebarTreeRow
            key={getShortKeyForEntity(key)}
            $marginLeft={indent}
            $canExpand={canExpand}
            onClick={handleClick}
        >
            {expandNode}
            {labelNode}
        </SidebarTreeRow>
    );

    return null;
};
